import { useAuthInterceptor } from "@logora/debate.auth.use_auth";
import { IconProvider } from "@logora/debate.icons.icon_provider";
import * as regularIcons from "@logora/debate.icons.regular_icons";
// i18n
import polyglotI18nProvider from "ra-i18n-polyglot";
import React from "react";
import {
	Admin,
	Resource,
	combineDataProviders,
	resolveBrowserLocale,
} from "react-admin";
import { IntlProvider } from "react-intl";
import { Dashboard } from "./Dashboard";
import MyLayout from "./Layout";
import { accountDataProvider, routes as accountRoutes } from "./account";
// dataProviders
import authProvider from "./authProvider";
import { Auth, routes as authenticationRoutes } from "./authentication";
import { axiosClient } from "./axiosClient";
// Imported routes
import { debateDataProvider, routes as debateRoutes } from "./debate";
import { ExportIcon, ExportList } from "./exports";
import locales from "./i18n";
import { routes as moderationRoutes } from "./moderation";
import {
	localesParliament,
	parliamentDataProvider,
	routes as parliamentRoutes,
} from "./parliament";
import { SettingsEdit, SettingsIcon } from "./settings";
import {
	socialNetworksDataProvider,
	socialNetworksRoutes,
} from "./social_networks";
import { routes as sourceRoutes } from "./source";
import { debateTheme } from "./themes/debateTheme";
import { parliamentTheme } from "./themes/parliamentTheme";
import { UserEdit, UserIcon, UserList } from "./users";

const messages = {
	fr: {
		...locales.french,
		resources: {
			...locales.french.resources,
			...localesParliament.resources,
		},
	},
	en: locales.english,
	es: locales.spanish,
	de: locales.german,
	it: locales.italian,
	pt: locales.portuguese,
	pt_BR: locales.portugueseBR,
};

const i18nProvider = polyglotI18nProvider(
	(locale) => (messages[locale] ? messages[locale] : messages.en),
	resolveBrowserLocale(),
);

const defaultDataProvider = Object.assign(
	debateDataProvider,
	accountDataProvider,
);

const dataProvider = combineDataProviders((resource) => {
	const hashIncludesParliament = window.location.hash.includes("parliament");
	const isParliamentResource =
		resource === "dossiers" ||
		resource === "scrutins" ||
		resource === "reunions";
	const isSocialNetworkResource = [
		"moderable_contents",
		"integrations",
		"batches",
		"moderable_sources",
		"posts",
	].includes(resource);

	if (hashIncludesParliament || isParliamentResource) {
		return resource.includes("alerts") ? defaultDataProvider : parliamentDataProvider;
	}

	if (isSocialNetworkResource) {
		return socialNetworksDataProvider;
	}

	return defaultDataProvider;
});

const getThemeByDomain = () => {
	const hostname = window.location.hostname;
	if (hostname.includes("legiwatch.fr") || process.env.REACT_APP_THEME === "parliament") {
		return parliamentTheme;
	}
	return debateTheme;
};

const App = () => {
	const tokenKey =
		process.env.NODE_ENV === "production"
			? "logora_admin_token"
			: "logora_admin_token_staging";

	useAuthInterceptor(axiosClient, process.env.REACT_APP_API_AUTH_URL, tokenKey);

	return (
		<IntlProvider locale="fr" onError={() => null}>
			<IconProvider library={regularIcons}>
				<Admin
					disableTelemetry
					loginPage={Auth}
					dashboard={Dashboard}
					layout={MyLayout}
					i18nProvider={i18nProvider}
					authProvider={authProvider}
					dataProvider={dataProvider}
					theme={getThemeByDomain()}
					darkTheme={null}
				>
					<Resource
						name="users"
						list={UserList}
						edit={UserEdit}
						icon={UserIcon}
					/>
					<Resource name="settings" list={SettingsEdit} icon={SettingsIcon} />
					<Resource name="exports" list={ExportList} icon={ExportIcon} />
					{debateRoutes}
					{parliamentRoutes}
					{moderationRoutes}
					{sourceRoutes}
					{accountRoutes}
					{socialNetworksRoutes}
					{authenticationRoutes}
				</Admin>
			</IconProvider>
		</IntlProvider>
	);
};

export default App;
